/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListIssuanceProfilesRequest,
  ListIssuanceProfilesSecurity,
} from "../models/operations/listissuanceprofiles.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildIssuanceProfilesQuery,
  IssuanceProfilesQueryData,
  prefetchIssuanceProfiles,
  queryKeyIssuanceProfiles,
} from "./issuanceProfiles.core.js";
export {
  buildIssuanceProfilesQuery,
  type IssuanceProfilesQueryData,
  prefetchIssuanceProfiles,
  queryKeyIssuanceProfiles,
};

export type IssuanceProfilesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listIssuanceProfiles authClients
 *
 * @remarks
 * List the issuance profiles for headless callers signing in through a sign-in. Requires org:read.
 */
export function useIssuanceProfiles(
  request: ListIssuanceProfilesRequest,
  security?: ListIssuanceProfilesSecurity | undefined,
  options?: QueryHookOptions<
    IssuanceProfilesQueryData,
    IssuanceProfilesQueryError
  >,
): UseQueryResult<IssuanceProfilesQueryData, IssuanceProfilesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildIssuanceProfilesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listIssuanceProfiles authClients
 *
 * @remarks
 * List the issuance profiles for headless callers signing in through a sign-in. Requires org:read.
 */
export function useIssuanceProfilesSuspense(
  request: ListIssuanceProfilesRequest,
  security?: ListIssuanceProfilesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    IssuanceProfilesQueryData,
    IssuanceProfilesQueryError
  >,
): UseSuspenseQueryResult<
  IssuanceProfilesQueryData,
  IssuanceProfilesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildIssuanceProfilesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setIssuanceProfilesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userSessionIssuerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: IssuanceProfilesQueryData,
): IssuanceProfilesQueryData | undefined {
  const key = queryKeyIssuanceProfiles(...queryKeyBase);

  return client.setQueryData<IssuanceProfilesQueryData>(key, data);
}

export function invalidateIssuanceProfiles(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userSessionIssuerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "authClients",
      "listIssuanceProfiles",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllIssuanceProfiles(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "listIssuanceProfiles"],
  });
}
